import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CreatePostModal = ({ isOpen, onClose, onSubmit }) => {
  const [postData, setPostData] = useState({
    title: '',
    content: '',
    category: 'Gift Wins',
    tags: [],
    images: [],
    giftDetails: {
      recipient: '',
      occasion: '',
      budget: ''
    },
    isAnonymous: false
  });
  
  const [currentTag, setCurrentTag] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const categories = [
    'Gift Wins',
    'Need Advice', 
    'Inspiration Boards',
    'Thank You Moments',
    'Cultural Celebrations'
  ];

  const occasions = [
    'Birthday',
    'Anniversary',
    'Wedding',
    'Graduation',
    'Holiday',
    'Just Because',
    'Other'
  ];

  const budgetRanges = [
    'Under $25',
    '$25 - $50',
    '$50 - $100',
    '$100 - $250',
    '$250 - $500',
    '$500+',
    'Prefer not to say'
  ];

  const handleInputChange = (field, value) => {
    setPostData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGiftDetailsChange = (field, value) => {
    setPostData(prev => ({
      ...prev,
      giftDetails: {
        ...prev.giftDetails,
        [field]: value
      }
    }));
  };

  const handleAddTag = () => {
    if (currentTag.trim() && !postData.tags.includes(currentTag.trim())) {
      setPostData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }));
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setPostData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageUpload = (files) => {
    const newImages = Array.from(files).map(file => URL.createObjectURL(file));
    setPostData(prev => ({
      ...prev,
      images: [...prev.images, ...newImages].slice(0, 4) // Limit to 4 images
    }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageUpload(e.dataTransfer.files);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(postData);
    onClose();
    // Reset form
    setPostData({
      title: '',
      content: '',
      category: 'Gift Wins',
      tags: [],
      images: [],
      giftDetails: {
        recipient: '',
        occasion: '',
        budget: ''
      },
      isAnonymous: false
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-accent-100">
          <h2 className="font-heading font-semibold text-xl text-accent-800">Create Post</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-accent-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Category
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => handleInputChange('category', category)}
                  className={`p-3 rounded-xl text-sm font-medium transition-all ${
                    postData.category === category
                      ? 'bg-primary text-white shadow-primary'
                      : 'bg-surface text-accent-600 hover:bg-accent-100'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Title
            </label>
            <Input
              type="text"
              placeholder="What's your gift story?"
              value={postData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              required
              className="w-full"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Tell your story
            </label>
            <textarea
              placeholder="Share the details of your gift experience, what worked, what didn't, and any advice for the community..."
              value={postData.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              required
              rows={4}
              className="w-full p-3 border border-accent-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary resize-none"
            />
          </div>

          {/* Gift Details */}
          <div className="bg-surface rounded-xl p-4 space-y-4">
            <h3 className="font-semibold text-accent-800">Gift Details (Optional)</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-accent-700 mb-2">
                  Recipient Relationship
                </label>
                <Input
                  type="text"
                  placeholder="e.g., Sister, Best Friend, Colleague"
                  value={postData.giftDetails.recipient}
                  onChange={(e) => handleGiftDetailsChange('recipient', e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-accent-700 mb-2">
                  Occasion
                </label>
                <select
                  value={postData.giftDetails.occasion}
                  onChange={(e) => handleGiftDetailsChange('occasion', e.target.value)}
                  className="w-full p-3 border border-accent-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">Select occasion</option>
                  {occasions.map((occasion) => (
                    <option key={occasion} value={occasion}>{occasion}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-2">
                Budget Range
              </label>
              <select
                value={postData.giftDetails.budget}
                onChange={(e) => handleGiftDetailsChange('budget', e.target.value)}
                className="w-full p-3 border border-accent-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Select budget range</option>
                {budgetRanges.map((range) => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Photos (Up to 4)
            </label>
            <div
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                dragActive
                  ? 'border-primary bg-primary-50' :'border-accent-300 hover:border-accent-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Icon name="Upload" size={32} className="text-accent-400 mx-auto mb-2" />
              <p className="text-accent-600 mb-2">Drag & drop images here, or click to select</p>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleImageUpload(e.target.files)}
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className="inline-block px-4 py-2 bg-primary text-white rounded-lg cursor-pointer hover:bg-primary-600 transition-colors"
              >
                Choose Files
              </label>
            </div>
            
            {/* Image Preview */}
            {postData.images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                {postData.images.map((image, index) => (
                  <div key={index} className="relative">
                    <Image
                      src={image}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-20 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => setPostData(prev => ({
                        ...prev,
                        images: prev.images.filter((_, i) => i !== index)
                      }))}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-error text-white rounded-full flex items-center justify-center hover:bg-error-600 transition-colors"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Tags
            </label>
            <div className="flex space-x-2 mb-2">
              <Input
                type="text"
                placeholder="Add a tag"
                value={currentTag}
                onChange={(e) => setCurrentTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                className="flex-1"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleAddTag}
                disabled={!currentTag.trim()}
              >
                Add
              </Button>
            </div>
            
            {postData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {postData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center space-x-1 px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-sm"
                  >
                    <span>#{tag}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-secondary-800"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Privacy Options */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="anonymous"
              checked={postData.isAnonymous}
              onChange={(e) => handleInputChange('isAnonymous', e.target.checked)}
              className="w-4 h-4 text-primary border-accent-300 rounded focus:ring-primary"
            />
            <label htmlFor="anonymous" className="text-sm text-accent-700">
              Post anonymously
            </label>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={!postData.title.trim() || !postData.content.trim()}
            >
              Share Post
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePostModal;